import { isEqual } from 'lodash';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type {
  RecipeIngredient,
  BookableTransportIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  FerryIngredient,
  FlightIngredient,
  isAvailableFerryTransportIngredientResponseWithStaticInfo,
  isAvailableFlightTransportIngredientResponseWithStaticInfo,
  isAvailableTransportIngredientResponseWithStaticInfo,
  isFlightIngredient,
} from '@apps/main/modules/recipe/ingredients';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import type {
  AlternativeItineraryFlightSegment,
  AvailableItineraryFerrySegment,
  AvailableItineraryFlightSegment,
  CarrierLogo,
  FerryCardDetails,
  FerryProductCardData,
  FerryUpsell,
  FlightCardDetails,
  FlightProductCardData,
  ItineraryBookableTransportSegment,
  PortDetails,
  ProductInfo,
  ProductTagWithIcon,
  UnavailableItineraryFerrySegment,
  UnavailableItineraryFlightSegment,
  FlightCarrierData,
} from '@apps/main/new-desktop/pages/tripOverview/models';
import {
  ItineraryFlightSegmentStatus,
  ItinerarySegmentType,
  ProductCardTopBarType,
  ProductCardType,
  UnavailabilityReason,
  FlightPriceType,
} from '@apps/main/new-desktop/pages/tripOverview/models';
import type { FlightIngredientFilters } from '@apps/main/modules/recipe/ingredients/flightIngredient';

import { getFormattedDateMonth, getHumanizedDuration } from '@libs/utils';
import { isFeatureToggleActive } from '@libs/feature-toggle';
import {
  isAlternativeTransportIngredientResponseWithStaticInfo,
  isTransportIngredientResponseWithStaticInfo,
} from '@libs/search-service-smart-search';
import { DateFormat, SemboDate } from '@libs/sembo-dates';
import type {
  AlternativeFlightIngredientResponseWithStaticInfo,
  AvailableFerryIngredientResponseWithStaticInfo,
  AvailableFlightIngredientResponseWithStaticInfo,
  AvailableTransportIngredientResponseWithStaticInfo,
  FerryTicketCategory,
  FlightIngredientResponseWithStaticInfo,
  ItineraryMainIngredientResponseWithStaticInfo,
  TransportIngredientResponseWithStaticInfo,
  TransportSegment,
  TransportStaticInfo,
  TransportVehicle,
  CarrierStaticInfo,
  CarrierName,
  TransportLeg,
  IngredientId,
} from '@libs/trip-planner-interfaces';
import {
  AvailabilityOrder,
  IngredientType,
  TransportVehicleType,
} from '@libs/trip-planner-interfaces';
import type { TagData } from '@libs/ui';

import { getChangeButtonText } from '../tripOverviewStoreLogic';

import {
  getFerryFilterPills,
  getFlightFilterPills,
} from './tripOverviewTransportFiltersPillsLogic';

const { t } = i18n.global;

export function getBookableTransportSegment(
  ingredient: ItineraryMainIngredientResponseWithStaticInfo,
  getIngredient: (id: string) => RecipeIngredient,
  isSuggestedTransport: (ingredientId: IngredientId) => boolean,
  ferryTicketCategoryDictionary:
    | Record<string, FerryTicketCategory[]>
    | undefined,
  userSelectedFerryTicketCategory:
    | Record<string, FerryTicketCategory>
    | undefined,
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  currency: string,
  selectedDate?: Date
): ItineraryBookableTransportSegment | null {
  if (isTransportIngredientResponseWithStaticInfo(ingredient)) {
    const transportIngredient = getIngredient(
      ingredient.ingredientId
    ) as BookableTransportIngredient;

    const productCardTopBarType = getProductCardTopBarType(
      isSuggestedTransport(ingredient.ingredientId),
      transportIngredient.availabilityOrder
    );
    const transport = getTripOverviewTransportSegment(
      ingredient,
      getIngredient,
      getFlightAllCarriers,
      getFerryAllCarriers,
      ferryTicketCategoryDictionary,
      userSelectedFerryTicketCategory,
      currency,
      productCardTopBarType,
      selectedDate
    );

    return transport;
  }
  return null;
}

export function getTripOverviewTransportSegment(
  transportIngredient: TransportIngredientResponseWithStaticInfo,
  getIngredient: (id: string) => RecipeIngredient,
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  getFerryAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  ferryTicketCategoryDictionary:
    | Record<string, FerryTicketCategory[]>
    | undefined,
  userSelectedFerryTicketCategory:
    | Record<string, FerryTicketCategory>
    | undefined,
  currency: string,
  productCardTopBarType?: ProductCardTopBarType,
  selectedDate?: Date
): ItineraryBookableTransportSegment | null {
  if (
    !isAvailableTransportIngredientResponseWithStaticInfo(
      transportIngredient
    ) ||
    isFeatureToggleActive('show-availability-errors')
  ) {
    const ingredient = getIngredient(
      transportIngredient.ingredientId
    ) as BookableTransportIngredient;

    const filtersPills = getFlightFilterPills(
      ingredient.filters,
      getFlightAllCarriers(ingredient.ingredientId),
      currency
    );

    switch (ingredient.ingredientType) {
      case IngredientType.Flight:
        if (
          isAlternativeTransportIngredientResponseWithStaticInfo(
            transportIngredient
          )
        ) {
          return getTripOverviewAlternativeFlightSegment(
            transportIngredient,
            ingredient,
            filtersPills,
            selectedDate
          );
        } else {
          return getTripOverviewUnavailableFlightSegment(
            ingredient,
            filtersPills,
            selectedDate
          );
        }

      case IngredientType.Ferry:
        return getTripOverviewUnavailableFerrySegment(ingredient, selectedDate);

      default:
        throw Error('Bookable transport segment not defined');
    }
  }
  if (
    isAvailableFlightTransportIngredientResponseWithStaticInfo(
      transportIngredient
    )
  ) {
    const ingredient = getIngredient(
      transportIngredient.ingredientId
    ) as BookableTransportIngredient;

    return getTripOverviewFlightSegment(
      getFlightAllCarriers,
      transportIngredient,
      ingredient.filters,
      currency,
      productCardTopBarType
    );
  }
  if (
    isAvailableFerryTransportIngredientResponseWithStaticInfo(
      transportIngredient
    )
  ) {
    const ingredient = getIngredient(
      transportIngredient.ingredientId
    ) as FerryIngredient;

    const filtersPills = getFerryFilterPills(
      ingredient.filters,
      getFerryAllCarriers(ingredient.ingredientId)
    );
    return getTripOverviewFerrySegment(
      transportIngredient,
      ferryTicketCategoryDictionary,
      userSelectedFerryTicketCategory,
      ingredient.allowVehicles,
      filtersPills,
      ingredient.fromPortCountry,
      ingredient.toPortCountry,
      productCardTopBarType
    );
  }
  return null;
}

function getTripOverviewUnavailableFerrySegment(
  ingredient: BookableTransportIngredient,
  selectedDate?: Date
): UnavailableItineraryFerrySegment {
  return {
    isAvailable: false,
    ingredientId: ingredient.ingredientId,
    segmentType: ItinerarySegmentType.Ferry,
    productCardData: null,
    reasons: getUnavailableTransportSegmentReasons(ingredient, selectedDate),
  };
}

function getTripOverviewUnavailableFlightSegment(
  ingredient: BookableTransportIngredient,
  filters: TagData[],
  selectedDate?: Date
): UnavailableItineraryFlightSegment {
  return {
    ingredientId: ingredient.ingredientId,
    statusType: ItineraryFlightSegmentStatus.NoAvailable,
    segmentType: ItinerarySegmentType.Flight,
    productCardData: null,
    reasons: getUnavailableTransportSegmentReasons(ingredient, selectedDate),
    filtersPills: filters,
    suggestionType: undefined,
  };
}

function getTripOverviewAlternativeFlightSegment(
  transportIngredient: AlternativeFlightIngredientResponseWithStaticInfo,
  ingredient: BookableTransportIngredient,
  filters: TagData[],
  selectedDate?: Date
): AlternativeItineraryFlightSegment {
  return {
    ingredientId: transportIngredient.ingredientId,
    statusType: ItineraryFlightSegmentStatus.AlternativeFlight,
    segmentType: ItinerarySegmentType.Flight,
    productCardData: getTripOverviewAlternativeFlightCard(transportIngredient),
    reasons: getUnavailableTransportSegmentReasons(ingredient, selectedDate),
    filtersPills: filters,
    suggestionType: undefined,
  };
}

export function getUnavailableTransportSegmentReasons(
  ingredient: BookableTransportIngredient,
  selectedDate?: Date
): UnavailabilityReason[] {
  const reasons: UnavailabilityReason[] = [];

  const defaultFilters = isFlightIngredient(ingredient)
    ? FlightIngredient.defaultFilters
    : FerryIngredient.defaultFilters;

  const hasSelectedProduct =
    !!ingredient.transportIds && ingredient.transportIds.length > 0;
  const hasSelectedFilters = !isEqual(ingredient.filters, defaultFilters);

  if (selectedDate) {
    reasons.push(UnavailabilityReason.SelectedDate);
  }

  if (hasSelectedProduct) {
    reasons.push(UnavailabilityReason.SelectedProduct);
  } else if (hasSelectedFilters) {
    reasons.push(UnavailabilityReason.SelectedFilters);
  }

  if (reasons.length === 0) {
    reasons.push(UnavailabilityReason.Suggested);
  }

  return reasons;
}

export function getUnavailableFlightMessage(
  reasons: UnavailabilityReason[]
): string {
  if (isEqual(reasons, [UnavailabilityReason.SelectedDate])) {
    return t('planner.no_availability.messages.flight_date_selected');
  }

  if (isEqual(reasons, [UnavailabilityReason.SelectedProduct])) {
    return t('planner.no_availability.messages.flight_selected');
  }

  if (isEqual(reasons, [UnavailabilityReason.SelectedFilters])) {
    return t('planner.no_availability.messages.flight_with_filters');
  }

  if (
    reasons.includes(UnavailabilityReason.SelectedDate) &&
    reasons.includes(UnavailabilityReason.SelectedProduct)
  ) {
    return t('planner.no_availability.messages.flight_selected_date_selected');
  }

  if (
    reasons.includes(UnavailabilityReason.SelectedDate) &&
    reasons.includes(UnavailabilityReason.SelectedFilters)
  ) {
    return t(
      'planner.no_availability.messages.flight_date_selected_with_filters'
    );
  }

  return t('notification.no_available_with_current_filters');
}

export function getUnavailableFerryMessage(
  reasons: UnavailabilityReason[]
): string {
  if (isEqual(reasons, [UnavailabilityReason.SelectedDate])) {
    return t('planner.no_availability.messages.ferry_date_selected');
  }

  if (isEqual(reasons, [UnavailabilityReason.SelectedProduct])) {
    return t('planner.no_availability.messages.ferry_selected');
  }

  if (isEqual(reasons, [UnavailabilityReason.SelectedFilters])) {
    return t('planner.no_availability.messages.ferry_with_filters');
  }

  if (
    reasons.includes(UnavailabilityReason.SelectedDate) &&
    reasons.includes(UnavailabilityReason.SelectedProduct)
  ) {
    return t('planner.no_availability.messages.ferry_selected_date_selected');
  }

  if (
    reasons.includes(UnavailabilityReason.SelectedDate) &&
    reasons.includes(UnavailabilityReason.SelectedFilters)
  ) {
    return t(
      'planner.no_availability.messages.ferry_date_selected_with_filters'
    );
  }

  return t('notification.no_available_with_current_filters');
}

export function getTripOverviewFlightSegment(
  getFlightAllCarriers: (ingredientId: IngredientId) => CarrierName[],
  transportIngredient: AvailableFlightIngredientResponseWithStaticInfo,
  filters: FlightIngredientFilters,
  currency: string,
  productCardTopBarType?: ProductCardTopBarType
): AvailableItineraryFlightSegment {
  let suggestionType: SuggestionType | undefined = undefined;
  if (productCardTopBarType !== ProductCardTopBarType.Selected) {
    suggestionType =
      productCardTopBarType === ProductCardTopBarType.Suggestion
        ? SuggestionType.MostPopular
        : SuggestionType.Cheapest;
  }

  const filtersPills = getFlightFilterPills(
    filters,
    getFlightAllCarriers(transportIngredient.ingredientId),
    currency
  );

  return {
    segmentType: ItinerarySegmentType.Flight,
    statusType: ItineraryFlightSegmentStatus.Available,
    productCardData: getTripOverviewFlightCard(transportIngredient),
    ingredientId: transportIngredient.ingredientId,
    suggestionType,
    filtersPills,
  };
}

export function getTripOverviewFlightCard(
  transportIngredient: AvailableFlightIngredientResponseWithStaticInfo
): FlightProductCardData {
  return getFlightProductCardContent(transportIngredient);
}

export function getTripOverviewAlternativeFlightCard(
  transportIngredient: AlternativeFlightIngredientResponseWithStaticInfo
): FlightProductCardData {
  return getAlternativeFlightProductCardContent(transportIngredient);
}

export function getProductCardTopBarType(
  isSuggestedTransport: boolean,
  ingredientSuggestionSortType: AvailabilityOrder
): ProductCardTopBarType | undefined {
  if (!isSuggestedTransport) {
    return ProductCardTopBarType.Selected;
  }
  return ingredientSuggestionSortType === AvailabilityOrder.Cheapest
    ? ProductCardTopBarType.Cheapest
    : ProductCardTopBarType.Suggestion;
}

export function getFlightProductCardContent(
  transportIngredient: AvailableFlightIngredientResponseWithStaticInfo
): FlightProductCardData {
  return {
    fromPort: getTransportPortDetails(
      transportIngredient.staticInfo,
      transportIngredient.departureIata,
      transportIngredient.departureDate,
      true
    ),
    toPort: getTransportPortDetails(
      transportIngredient.staticInfo,
      transportIngredient.arrivalIata,
      transportIngredient.arrivalDate,
      true
    ),
    details: getFlightTransportCardDetails(transportIngredient),
  };
}
export function getAlternativeFlightProductCardContent(
  transportIngredient: AlternativeFlightIngredientResponseWithStaticInfo
): FlightProductCardData {
  return {
    fromPort: getTransportPortDetails(
      transportIngredient.staticInfo,
      transportIngredient.alternativeSuggestion.departureIata,
      transportIngredient.alternativeSuggestion.departureDate,
      true
    ),
    toPort: getTransportPortDetails(
      transportIngredient.staticInfo,
      transportIngredient.alternativeSuggestion.arrivalIata,
      transportIngredient.alternativeSuggestion.arrivalDate,
      true
    ),
    details: getAlternativeFlightTransportCardDetails(transportIngredient),
  };
}

export function getFlightTransportCardDetails(
  transportIngredient: AvailableFlightIngredientResponseWithStaticInfo
): FlightCardDetails {
  return {
    date: new SemboDate(transportIngredient.date).format(DateFormat.DateMonth),
    time: getHumanizedDuration(transportIngredient.durationInMinutes, 'minute'),
    carrier: getFlightCarrierData(transportIngredient),
    mainDetails: getFlightDescriptionFromNumberOfStops(
      transportIngredient.numberOfStops
    ),
    productLabels: getFlightProductLabels(),
    changeButtonText: getChangeButtonText(ProductCardType.Flight),
    warningTags: getFlightWarningTags(transportIngredient),
    flightPriceType: getFlightPriceType(
      transportIngredient.legIndex,
      transportIngredient.legs
    ),
  };
}
export function getAlternativeFlightTransportCardDetails(
  transportIngredient: AlternativeFlightIngredientResponseWithStaticInfo
): FlightCardDetails {
  const durationInMinutes =
    transportIngredient.alternativeSuggestion.durationInMinutes;

  return {
    date: new SemboDate(transportIngredient.date).format(DateFormat.DateMonth),
    carrier: getFlightCarrierData(transportIngredient),
    time: getHumanizedDuration(durationInMinutes, 'minute'),
    mainDetails: getFlightDescriptionFromNumberOfStops(
      transportIngredient.alternativeSuggestion.numberOfStops
    ),
    productLabels: getFlightProductLabels(),
    changeButtonText: getChangeButtonText(ProductCardType.Flight),
    warningTags: getFlightWarningTags(transportIngredient),
    flightPriceType: getFlightPriceType(
      transportIngredient.alternativeSuggestion.legIndex,
      transportIngredient.alternativeSuggestion.legs
    ),
  };
}

function getFlightWarningTags(
  ingredient: FlightIngredientResponseWithStaticInfo
): ProductTagWithIcon[] {
  const tags: ProductTagWithIcon[] = [];

  let departureDate: SemboDate;
  let arrivalDate: Date;
  if (isAvailableFlightTransportIngredientResponseWithStaticInfo(ingredient)) {
    departureDate = new SemboDate(ingredient.departureDate);
    arrivalDate = new Date(ingredient.arrivalDate);
  } else {
    if (isAlternativeTransportIngredientResponseWithStaticInfo(ingredient)) {
      departureDate = new SemboDate(
        ingredient.alternativeSuggestion.departureDate
      );
      arrivalDate = new Date(ingredient.alternativeSuggestion.arrivalDate);
    } else {
      return [];
    }
  }

  const isTransportArrivingSameDay = departureDate.isSameDay(arrivalDate);
  const airportChangeWarning = getAirportChangeWarningTag(ingredient);

  if (!isTransportArrivingSameDay) {
    const description = `${t(
      'planner.this_flight_arrives_on'
    )} ${getFormattedDateMonth(arrivalDate)}`;
    tags.push({
      description,
      svgIconType: 'calendarOutlined',
    });
  }

  if (airportChangeWarning) {
    tags.push(airportChangeWarning);
  }

  return tags;
}

function getFlightPriceType(
  legIndex: number,
  legs: TransportLeg[]
): FlightPriceType {
  if (legIndex === 1) {
    return FlightPriceType.IncludedOnOutbound;
  }

  if (legs.length === 2) {
    return FlightPriceType.OutboundHomebound;
  }

  return FlightPriceType.OneWayOnly;
}

function getAirportChangeWarningTag(
  ingredient: FlightIngredientResponseWithStaticInfo
): ProductTagWithIcon | undefined {
  let legs;

  if (isAvailableFlightTransportIngredientResponseWithStaticInfo(ingredient)) {
    legs = ingredient.legs;
  } else {
    if (isAlternativeTransportIngredientResponseWithStaticInfo(ingredient)) {
      legs = ingredient.alternativeSuggestion.legs;
    }
  }

  if (!legs) {
    return;
  }
  const hasAirportChangeWarning = legs.some(({ segments }) =>
    hasDifferentIataCodesAtAnySegment(segments)
  );

  if (hasAirportChangeWarning) {
    return {
      svgIconType: 'change',
      description: t('planner.airport_change'),
    };
  }

  return;
}

function hasDifferentIataCodesAtAnySegment(
  segments: TransportSegment[]
): boolean {
  return segments.some((segment, index) => {
    const nextSegment = segments[index + 1];
    return nextSegment && segment.arrivalIata !== nextSegment.departureIata;
  });
}

export function getFlightCarrierData(
  transportIngredient:
    | AvailableFlightIngredientResponseWithStaticInfo
    | AlternativeFlightIngredientResponseWithStaticInfo
): FlightCarrierData | null {
  const carrierStaticInfo = getMainCarrierStaticInfo(transportIngredient);

  if (!carrierStaticInfo) {
    return null;
  }

  return {
    logo: {
      urls: carrierStaticInfo.logo ? [carrierStaticInfo.logo] : [],
    },
    name: carrierStaticInfo.name,
  };
}

export function getFlightProductLabels(): ProductInfo[] {
  // TODO: get flight labels
  const labels: ProductInfo[] = [];
  return labels;
}

export function getFlightDescriptionFromNumberOfStops(
  numberOfStops: number
): string {
  return numberOfStops === 0
    ? t('planner.direct_flight')
    : t('planner.number_of_stops', [numberOfStops], numberOfStops);
}

export function getMainCarrierStaticInfo(
  transportIngredient:
    | AvailableTransportIngredientResponseWithStaticInfo
    | AlternativeFlightIngredientResponseWithStaticInfo,
  isFerryIngredient?: boolean
): CarrierStaticInfo | null {
  let firstCarrierCode;

  if (
    isAlternativeTransportIngredientResponseWithStaticInfo(transportIngredient)
  ) {
    firstCarrierCode = isFerryIngredient
      ? transportIngredient.alternativeSuggestion.carriers[0]
      : transportIngredient.alternativeSuggestion.legs[
          transportIngredient.alternativeSuggestion.legIndex
        ]?.platingCarrier ||
        transportIngredient.alternativeSuggestion.carriers[0];
  } else {
    firstCarrierCode = isFerryIngredient
      ? transportIngredient.carriers[0]
      : transportIngredient.legs[transportIngredient.legIndex]
          ?.platingCarrier || transportIngredient.carriers[0];
  }

  if (!firstCarrierCode) {
    return null;
  }

  const carrierStaticInfo =
    transportIngredient.staticInfo.carriers[firstCarrierCode];

  if (!carrierStaticInfo) {
    return null;
  }

  return carrierStaticInfo;
}

export function getTripOverviewFerrySegment(
  transportIngredient: AvailableFerryIngredientResponseWithStaticInfo,
  ferryTicketCategoryDictionary:
    | Record<string, FerryTicketCategory[]>
    | undefined,
  userSelectedFerryTicketCategory:
    | Record<string, FerryTicketCategory>
    | undefined,
  allowVehicles: boolean,
  filtersPills: TagData[],
  fromPortCountry?: string,
  toPortCountry?: string,
  productCardTopBarType?: ProductCardTopBarType
): AvailableItineraryFerrySegment {
  let suggestionType: SuggestionType | undefined = undefined;
  if (productCardTopBarType !== ProductCardTopBarType.Selected) {
    suggestionType =
      productCardTopBarType === ProductCardTopBarType.Suggestion
        ? SuggestionType.MostPopular
        : SuggestionType.Cheapest;
  }

  return {
    segmentType: ItinerarySegmentType.Ferry,
    productCardData: getTripOverviewFerryCard(
      transportIngredient,
      ferryTicketCategoryDictionary,
      userSelectedFerryTicketCategory,
      allowVehicles,
      fromPortCountry,
      toPortCountry
    ),
    suggestionType,
    isAvailable: true,
    ingredientId: transportIngredient.ingredientId,
    filtersPills,
  };
}

export function getTripOverviewFerryCard(
  ferryIngredient: AvailableFerryIngredientResponseWithStaticInfo,
  ferryTicketCategoryDictionary:
    | Record<string, FerryTicketCategory[]>
    | undefined,
  userSelectedFerryTicketCategory:
    | Record<string, FerryTicketCategory>
    | undefined,
  allowVehicles: boolean,
  fromPortCountry?: string,
  toPortCountry?: string
): FerryProductCardData {
  return getFerryProductCardContent(
    ferryIngredient,
    ferryTicketCategoryDictionary,
    userSelectedFerryTicketCategory,
    allowVehicles,
    fromPortCountry,
    toPortCountry
  );
}

export function getFerryProductCardContent(
  transportIngredient: AvailableFerryIngredientResponseWithStaticInfo,
  ferryTicketCategoryDictionary:
    | Record<string, FerryTicketCategory[]>
    | undefined,
  userSelectedFerryTicketCategory:
    | Record<string, FerryTicketCategory>
    | undefined,
  allowVehicles: boolean,
  fromPortCountry?: string,
  toPortCountry?: string
): FerryProductCardData {
  return {
    fromPort: getTransportPortDetails(
      transportIngredient.staticInfo,
      transportIngredient.departureIata,
      transportIngredient.departureDate,
      false,
      fromPortCountry
    ),
    toPort: getTransportPortDetails(
      transportIngredient.staticInfo,
      transportIngredient.arrivalIata,
      transportIngredient.arrivalDate,
      false,
      toPortCountry
    ),
    details: getFerryTransportCardDetails(transportIngredient),
    upsell: getFerryProductCardUpsellDetails(
      transportIngredient,
      ferryTicketCategoryDictionary,
      userSelectedFerryTicketCategory,
      allowVehicles
    ),
  };
}

export function getFerryProductCardUpsellDetails(
  transportIngredient: AvailableFerryIngredientResponseWithStaticInfo,
  ferryTicketCategoryDictionary:
    | Record<string, FerryTicketCategory[]>
    | undefined,
  userSelectedFerryTicketCategory:
    | Record<string, FerryTicketCategory>
    | undefined,
  allowVehicles: boolean
): FerryUpsell {
  const hasUserSelectedFerryTicketCategory =
    userSelectedFerryTicketCategory?.[transportIngredient.addToBasketToken] !==
    undefined;

  const ticket = hasUserSelectedFerryTicketCategory
    ? userSelectedFerryTicketCategory?.[transportIngredient.addToBasketToken]
    : ferryTicketCategoryDictionary?.[
        transportIngredient.addToBasketToken
      ]?.[0];
  return {
    token: transportIngredient.addToBasketToken,
    vehicles: transportIngredient.vehicles,
    allowVehicles,
    cabin: transportIngredient.isCabinMandatory,
    ingredientId: transportIngredient.ingredientId,
    transportationName: transportIngredient.transportationName,
    ticket: {
      type: ticket?.type || 'Economy',
      price: hasUserSelectedFerryTicketCategory ? ticket?.price.amount : 0,
      basePrice:
        ferryTicketCategoryDictionary?.[
          transportIngredient.addToBasketToken
        ]?.[0]?.price.amount,
    },
  };
}

export function getVehiclesText(
  vehicles: string[],
  isSmallDevice: boolean
): string[] {
  if (isSmallDevice) {
    return vehicles;
  }

  const itemsToDisplay = 2;
  const result: string[] = [];
  let moreVehiclesAmount = 0;

  vehicles.forEach((vehicle, index) => {
    if (index < itemsToDisplay) {
      result.push(vehicle);
    } else {
      const position = vehicle.indexOf('x');
      if (position > -1) {
        moreVehiclesAmount += Number(vehicle.slice(0, position));
      }
    }
  });

  if (moreVehiclesAmount) {
    const moreVehicles = `+${moreVehiclesAmount} ${t(
      'planner.transport.more_vehicles',
      moreVehiclesAmount
    ).toLocaleLowerCase()}`;

    result.push(moreVehicles);
  }

  return result;
}

export function getFerryProductCardUpsellVehicles(
  transportIngredient: AvailableFerryIngredientResponseWithStaticInfo,
  ferryVehicleTypes: TransportVehicle[] | undefined
): string[] {
  const vehicleMap = new Map();
  if (!ferryVehicleTypes) {
    return [];
  }
  ferryVehicleTypes.forEach((vehicleType) => {
    vehicleMap.set(vehicleType.id, vehicleType.description);
  });
  if (transportIngredient.vehicles.length === 0) {
    return [vehicleMap.get(TransportVehicleType.None)];
  }
  const vehicles = transportIngredient.vehicles.map((vehicle) =>
    vehicleMap.get(vehicle)
  );

  const groupedVehicles = new Map();

  vehicles.forEach((vehicle) => {
    if (groupedVehicles.has(vehicle)) {
      groupedVehicles.set(vehicle, groupedVehicles.get(vehicle) + 1);
    } else {
      groupedVehicles.set(vehicle, 1);
    }
  });
  return Array.from(
    groupedVehicles,
    ([description, amount]) => `${amount}x ${description}`
  );
}

export function getFerryTransportCardDetails(
  transportIngredient: AvailableFerryIngredientResponseWithStaticInfo
): FerryCardDetails {
  return {
    date: new SemboDate(transportIngredient.date).format(DateFormat.DateMonth),
    logo: getFerryCarrierLogo(transportIngredient),
    time: getHumanizedDuration(transportIngredient.durationInMinutes, 'minute'),
    mainDetails: undefined,
    changeButtonText: getChangeButtonText(ProductCardType.Ferry),
    warningTags: getFerryWarningTags(transportIngredient),
    shipName: transportIngredient.transportationName,
    carrier: transportIngredient.carriers[0],
  };
}

export function getFerryCarrierLogo(
  transportIngredient: AvailableFerryIngredientResponseWithStaticInfo
): CarrierLogo {
  const carrierStaticInfo = getMainCarrierStaticInfo(transportIngredient, true);

  // FYI: Additional check for empty logo fallback.
  if (!carrierStaticInfo || !carrierStaticInfo.logo) {
    return { urls: [] };
  }

  return {
    urls: [carrierStaticInfo.logo].filter((logo) => !logo.includes('kiwi')),
  };
}

function getFerryWarningTags(
  ingredient: AvailableFerryIngredientResponseWithStaticInfo
): ProductTagWithIcon[] {
  const tags: ProductTagWithIcon[] = [];
  const departureDate = new SemboDate(ingredient.departureDate);
  const arrivalDate = ingredient.arrivalDate;
  const isTransportArrivingSameDay = departureDate.isSameDay(arrivalDate);

  if (!isTransportArrivingSameDay) {
    const description = `${t(
      'planner.this_ferry_arrives_on'
    )} ${getFormattedDateMonth(ingredient.arrivalDate)}`;
    tags.push({
      description,
      svgIconType: 'calendarOutlined',
    });
  }

  return tags;
}

export function getTransportPortDetails(
  staticInfo: TransportStaticInfo,
  iata: string,
  date: Date,
  shouldIncludeIata: boolean,
  portCountry?: string
): PortDetails {
  const portStaticInfo = staticInfo.ports[iata];
  const transportName = portStaticInfo?.position.name
    ? `${portStaticInfo?.position.name}`
    : '';
  const countryName = portCountry
    ? `${transportName}, ${portCountry}`
    : transportName;
  const result: PortDetails = {
    name: transportName,
    time: new SemboDate(date).format(DateFormat.Time),
    country: countryName,
  };

  if (shouldIncludeIata) {
    result.code = iata;
  }
  return result;
}
